import { useEffect, useState } from 'react';
import { getConstituencyWiseResults } from '@api/getConstituencyWiseResults';
import { autoSlotSelector, numberLanguageFormatter } from '@utils/utils';
import { useCustomParams } from './useCustomParams';
import {
  ConstituencyWiseResultsSettingsType,
  ConstituencyWiseResultsType,
} from '@type/constituencyWiseResults';

export interface useConstituencyWiseResultsType {
  constituencyWiseResults: any[];
  isLoading: boolean;
  handleQuery: (name: string, value: string | number) => void;
  multipleQuery: (data: { division: string; timeSlot: string }) => void;
  query: {
    division: string;
    timeSlot: string;
    percentage: number | string;
  };
}

function mapConstituencyWiseResult(
  data: ConstituencyWiseResultsSettingsType[],
) {
  const mappedData = data?.map((item: ConstituencyWiseResultsSettingsType) => ({
    id: item?.settings_code,
    constituencyName: `${numberLanguageFormatter(
      item?.settings_code || '',
    )} - ${item?.settings_name || ''}`,
    zilla_name: item?.zilla_name,
    done_voting_center: numberLanguageFormatter(item?.done_center_count || 0),
    vote_count_value: numberLanguageFormatter(item?.total_vote_cast || 0),
    percentage: `${numberLanguageFormatter(item?.percentage || 0)} %`,
    className: item?.percentage
      ? `${
          item?.percentage > 0
            ? item?.percentage >= 50
              ? item?.percentage >= 80
                ? 'bg-success'
                : 'bg-info'
              : 'bg-light'
            : ''
        }`
      : '',
  }));

  return mappedData;
}
export const useConstituencyWiseResults =
  (): useConstituencyWiseResultsType => {
    const [constituencyWiseResults, setConstituencyWiseResults] = useState<any>(
      [],
    );

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [query, setQuery] = useState<{
      division: string;
      timeSlot: string;
      percentage: number | string;
      election_schedule_id: string;
    }>({
      division: '',
      timeSlot: autoSlotSelector().sendSlotRange,
      percentage: '',
      election_schedule_id: '',
    });

    const { searchParams } = useCustomParams();
    const electionScheduleId = searchParams.get('election_schedule_id') || '';

    const handleQuery = (name: string, value: string | number) => {
      setQuery((prev) => {
        return {
          ...prev,
          [`${name}`]: value,
        };
      });
    };

    const multipleQuery = (data: { division: string; timeSlot: string }) => {
      setQuery((prev) => {
        return {
          ...prev,
          division: data.division,
          timeSlot: data.timeSlot,
        };
      });
    };

    useEffect(() => {
      if (query?.division && query?.timeSlot && electionScheduleId) {
        setIsLoading(true);
        getConstituencyWiseResults({
          ...query,
          election_schedule_id: electionScheduleId,
        })
          .then((data: { data: ConstituencyWiseResultsType }) => {
            const modifiedData = mapConstituencyWiseResult(
              data?.data?.settings,
            );
            setConstituencyWiseResults(modifiedData || []);
            setIsLoading(false);
          })
          .catch((err) => {
            setIsLoading(false);
          });
      }
    }, [query, electionScheduleId]);

    return {
      constituencyWiseResults,
      isLoading,
      query,
      handleQuery,
      multipleQuery,
    };
  };
